/**
 * Phase 2.5: Operative Dictation Types
 * 
 * Surgeon-first, revenue-safe dictation experience.
 * LINK captures and structures — never invents or rewrites.
 */

export type DictationStep = 'dictating' | 'reviewing' | 'confirming' | 'attesting';

export type DictationSourceType = 'dictation' | 'demo_preloaded';

export interface DictationSession {
  id: string;
  authorizationId: string;
  caseNumber: string;
  surgeonId: string;
  /** Raw transcript text — immutable after signing */
  rawTranscript: string;
  /** Structured note sections derived from transcript */
  structuredNote: StructuredOpNote;
  /** Documentation completeness indicators */
  completeness: DocumentationCompleteness;
  /** Procedure confirmations from surgeon */
  procedureConfirmations: ProcedureConfirmation[];
  /** Attestation state */
  attestation: DictationAttestation;
  /** Timestamps */
  startedAt: string;
  completedAt?: string;
  signedAt?: string;
  /** Source type */
  sourceType: DictationSourceType;
}

export interface StructuredOpNote {
  indications: string;
  findings: string;
  procedures: string;
  implants: string;
  closure: string;
  complications: string;
  estimatedBloodLoss: string;
  anesthesia: string;
  specimens: string;
  fullText: string;
}

export const EMPTY_STRUCTURED_NOTE: StructuredOpNote = {
  indications: '',
  findings: '',
  procedures: '',
  implants: '',
  closure: '',
  complications: '',
  estimatedBloodLoss: '',
  anesthesia: '',
  specimens: '',
  fullText: '',
};

export interface DocumentationCompleteness {
  primaryProcedureDetected: boolean;
  ancillaryProceduresDetected: boolean;
  separateCompartmentIndicator: boolean;
  lateralityDetected: boolean;
  assistantSurgeonJustification: boolean;
  /** Detected CPT-related keywords */
  detectedKeywords: string[];
}

export const EMPTY_COMPLETENESS: DocumentationCompleteness = {
  primaryProcedureDetected: false,
  ancillaryProceduresDetected: false,
  separateCompartmentIndicator: false,
  lateralityDetected: false,
  assistantSurgeonJustification: false,
  detectedKeywords: [],
};

export type ConfirmationStatus = 'confirmed_performed' | 'not_performed' | 'needs_review' | 'pending';

export interface ProcedureConfirmation {
  cptCode: string;
  description: string;
  status: ConfirmationStatus;
  confirmedAt?: string;
}

export interface DictationAttestation {
  attested: boolean;
  attestedAt?: string;
  attestedBy?: string;
  attestationText: string;
}

export const DICTATION_ATTESTATION_TEXT =
  'I attest this operative note accurately reflects the care provided.';

/** Demo dictation transcript for preloading */
export const DEMO_DICTATION_TRANSCRIPT = `This is Dr. Larkin dictating the operative report for the right total knee arthroplasty.

Indications: The patient is a 65-year-old with severe right knee osteoarthritis with grade IV bone-on-bone changes on weight-bearing radiographs. Conservative treatment including physical therapy, activity modification, NSAIDs, and intra-articular injections have all failed to provide adequate relief.

Findings: Upon exposure, significant tricompartmental arthritis was identified. The medial compartment showed grade IV chondromalacia with exposed subchondral bone. The lateral compartment showed grade III changes. The patellofemoral joint showed grade III changes centrally. A complex degenerative tear of the posterior horn of the medial meniscus was also noted.

Procedure: The patient was brought to the operating room and placed supine. After induction of general anesthesia, the right lower extremity was prepped and draped in the usual sterile fashion. A tourniquet was applied to the right thigh and inflated to 275 millimeters of mercury.

A standard midline incision was made over the anterior right knee. Medial parapatellar arthrotomy was performed. The patella was everted. The knee was flexed.

Tibial preparation was performed using the extramedullary guide. The tibial plateau was resected perpendicular to the mechanical axis with a 3-degree posterior slope. Femoral preparation was performed using the intramedullary guide with 5 degrees of valgus. Trial components were placed with excellent alignment and stability in both flexion and extension.

After pulsatile lavage, cement was mixed and applied. The Stryker Triathlon size 5 femoral component was cemented into position. The size 4 tibial baseplate was cemented. A 10-millimeter polyethylene insert was locked into place. Patella resurfacing was performed using a 32-millimeter all-polyethylene dome component.

Implants used: Stryker Triathlon Total Knee System. Femoral component size 5 cemented. Tibial baseplate size 4 cemented. Polyethylene insert 10 millimeters. Patella 32 millimeters all-polyethylene cemented.

Closure: The tourniquet was released after 58 minutes. Hemostasis was achieved with electrocautery. The wound was copiously irrigated with three liters of normal saline. The arthrotomy was closed with interrupted number 1 Vicryl sutures. The subcutaneous tissue was closed with 2-0 Vicryl. The skin was closed with surgical staples. Sterile dressing was applied.

Estimated blood loss: approximately 125 milliliters.
Complications: None.
Specimens: None.

The patient tolerated the procedure well and was transferred to the recovery room in stable condition.

End of dictation.`;
